import { useEffect } from 'react';
import { Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import Chip from '@/components/General/Chip';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import CareEpisodeView from '@/components/FollowUp/Care/CareEpisodeView';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule, isFreeOrg } from '@/utils/organizationEntitlements';
import { extractFollowUpCode } from '@/data/followUp';
import { parseTimeFrame } from '@/utils/dater';

/**
 * /follow-up/care/[careEpisodeID] — the template-free care episode surface
 * (§25.9). Care is an episode route, NOT a Path enrollment: header shows
 * the person + episode status, and CareEpisodeView renders the shared
 * activity surface (details, connector, touches, planned work, meals,
 * close review).
 */
const STATUS_META = {
    ACTIVE: { label: 'Active', colorVariant: 'paths', icon: 'heart-outline' },
    CLOSED: { label: 'Closed', colorVariant: 'textSecondary', icon: 'archive-outline' },
    CANCELLED: { label: 'Cancelled', colorVariant: 'textSecondary', icon: 'close-circle-outline' },
};

export default function FollowUpCareEpisodeScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { careEpisodeID } = useLocalSearchParams();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canView = !!loggedInUser?.can?.('View Follow Up');
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const gated = !!loggedInUser && canView && moduleAllowed;

    const { data, isLoading, isError, error, isRefetching, refetch } = Query.followUp.careEpisode({
        organizationID,
        careEpisodeID,
        options: { enabled: gated },
    });

    const episode = data?.episode;
    const meta = STATUS_META[episode?.status] || STATUS_META.ACTIVE;

    useEffect(() => {
        navigation.setOptions({ title: episode?.personName ? `${episode.personName} — Care` : 'Care Episode' });
    }, [navigation, episode?.personName]);

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.careEpisode(organizationID, careEpisodeID) });
        queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'careEpisodes'] });
        refetch();
    };

    if (loggedInUser && (!canView || !moduleAllowed)) {
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={!moduleAllowed && isFreeOrg(entitlements)
                        ? 'Follow Up is not available on this plan'
                        : "You don't have permission to view Follow Up care."}
                />
            </View>
        );
    }

    if (isError) {
        const code = extractFollowUpCode(error) || error?.followUpCode;
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={code === 'FOLLOW_UP_DENIED'
                        ? "You don't have permission to view this care episode."
                        : code === 'FOLLOW_UP_NOT_FOUND'
                            ? "That care episode couldn't be found. It may have been closed."
                            : 'Something went wrong loading the care episode. Pull to refresh to try again.'}
                />
            </View>
        );
    }

    const summaryLine = episode
        ? [
            `Started ${parseTimeFrame(episode.startedAt)}`,
            episode.closedAt ? `closed ${parseTimeFrame(episode.closedAt)}` : null,
        ].filter(Boolean).join(' · ')
        : null;

    return (
        <View style={styles.container}>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refresh} />}
            >
                {isLoading || !episode ? (
                    <View style={styles.loadingWrap}><ActivityIndicator size="large" /></View>
                ) : (
                    <>
                        <View style={styles.header} testID="care-episode-header">
                            <Pressable
                                testID="care-episode-person-link"
                                accessibilityRole="link"
                                onPress={() => router.push(`/people/${episode.personID}?tab=followup`)}
                            >
                                <Text style={styles.personName} numberOfLines={1}>
                                    {episode.personName || 'Unknown person'}
                                </Text>
                            </Pressable>
                            <View style={styles.chipsRow}>
                                <Chip icon={meta.icon} colorVariant={meta.colorVariant}>{meta.label}</Chip>
                                <Chip icon="heart-outline" colorVariant="textTertiary">Care episode</Chip>
                            </View>
                            {!!summaryLine && <Text style={styles.metaLine}>{summaryLine}</Text>}
                        </View>

                        <CareEpisodeView
                            organizationID={organizationID}
                            episode={episode}
                            care={data}
                            loggedInUser={loggedInUser}
                            canManage={canManage}
                        />
                    </>
                )}
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: { flex: 1 },
    scroll: { flex: 1 },
    content: { padding: 12, paddingBottom: 60, gap: 10, maxWidth: 700, width: '100%', alignSelf: 'center' },
    deniedWrap: { flex: 1, justifyContent: 'center' },
    loadingWrap: { padding: 40, alignItems: 'center' },
    header: { paddingHorizontal: 4, paddingTop: 4, gap: 4 },
    personName: { fontSize: 20, fontFamily: fonts.heading, color: colors.text },
    chipsRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 2 },
    metaLine: { fontSize: 12, color: colors.textSecondary },
}));
